import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { FormInput, useToast } from '@podus/ui';
import AuthLayout from '@/components/AuthLayout';
import { api, ApiError } from '@/lib/api';

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get('token') ?? '';
    const navigate = useNavigate();
    const toast = useToast();

    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;

        if (password !== passwordConfirm) {
            setError('Passwords do not match');
            return;
        }

        setError('');
        setBusy(true);
        try {
            await api.post('/ambassador/reset-password', { token, password, passwordConfirm });
            toast.success('Password updated — you can sign in now');
            navigate('/sign-in', { replace: true });
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Could not reset your password. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    if (!token) {
        return (
            <AuthLayout
                title="This reset link is invalid"
                subtitle="Request a new one and we'll email you a fresh link."
                footer={
                    <Link to="/forgot-password" className="font-bold text-primary">
                        Request a new link
                    </Link>
                }
            >
                <div />
            </AuthLayout>
        );
    }

    return (
        <AuthLayout title="Choose a new password" subtitle="At least 8 characters, with a letter and a number.">
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {error && (
                    <div
                        role="alert"
                        className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
                    >
                        {error}
                    </div>
                )}

                <FormInput
                    id="reset-password"
                    name="password"
                    type="password"
                    title="New password"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    required
                    variant="white"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />

                <FormInput
                    id="reset-password-confirm"
                    name="passwordConfirm"
                    type="password"
                    title="Confirm new password"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    required
                    variant="white"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    error={passwordConfirm && password !== passwordConfirm ? 'Passwords do not match' : undefined}
                />

                <button
                    type="submit"
                    disabled={busy || !password}
                    className="mt-2 h-12 w-full rounded-[10px] bg-secondary text-[14px] font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {busy ? 'Updating…' : 'Update password'}
                </button>
            </form>
        </AuthLayout>
    );
}
